import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { StatusBadgeWithLabel } from './StatusBadge';
import { cn } from '@/lib/utils';
import type { PersonStatus } from './StatusBadge';

interface PersonRowProps {
  person: {
    id: number;
    first_name?: string;
    last_name?: string;
    status?: string;
  };
  isDraft?: boolean;
  isMarkedForRemoval?: boolean;
  onRemove?: () => void;
  className?: string;
}

export function PersonRow({ 
  person, 
  isDraft = false, 
  isMarkedForRemoval = false,
  onRemove,
  className 
}: PersonRowProps) {
  const getInitials = () => {
    const first = person.first_name?.[0] || '';
    const last = person.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getStatusStyle = () => {
    if (isDraft || isMarkedForRemoval) {
      return 'bg-status-draft/10 border-status-draft/30 border-dashed';
    }
    
    switch (person.status) {
      case 'bekreftet':
        return 'bg-status-confirmed/10 border-status-confirmed/30';
      case 'planlagt':
      case 'ikke_svart':
        return 'bg-status-waiting/10 border-status-waiting/30';
      case 'avvist':
        return 'bg-status-declined/10 border-status-declined/30';
      default:
        return 'bg-muted border-border';
    }
  };

  const getAvatarColor = () => {
    if (isDraft || isMarkedForRemoval) return 'bg-status-draft text-status-draft-foreground';
    return 'bg-primary text-primary-foreground';
  };

  return (
    <div 
      className={cn(
        'flex items-center justify-between p-2 sm:p-3 rounded-lg border-2 transition-colors gap-2',
        getStatusStyle(),
        className
      )}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Avatar className="h-7 w-7 sm:h-8 sm:w-8 border-2 border-background shadow-sm shrink-0">
          <AvatarFallback className={cn('text-xs font-bold', getAvatarColor())}>
            {getInitials()}
          </AvatarFallback>
        </Avatar>
        <span className="font-semibold text-xs sm:text-sm text-foreground truncate">
          {person.first_name} {person.last_name}
        </span>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {isDraft ? (
          <span className="font-semibold text-xs sm:text-sm text-status-draft whitespace-nowrap">
            Utkast
          </span>
        ) : isMarkedForRemoval ? (
          <span className="font-semibold text-xs sm:text-sm text-status-draft whitespace-nowrap">
            Fjernes
          </span>
        ) : person.status && (
          <StatusBadgeWithLabel status={person.status as PersonStatus} className="text-xs" />
        )}
        
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className={cn(
              'h-7 w-7 sm:h-8 sm:w-8 hover:bg-destructive/10 shrink-0',
              (isDraft || isMarkedForRemoval) && 'hover:bg-status-draft/20'
            )}
          >
            <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

